import { getDataGridToolbarProps } from 'features/shared/utility/dataGridToolbarHelper';
import usePermissions from 'features/shared/hooks/usePermissions';
import { PERMISSION_RESOURCES } from 'features/shared/constants/permissionResources';
import FormSpinner from 'components/FormSpinner';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Box } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import { CommonDataGrid } from 'features/shared/components/table';
import { RMSConfirmationDialog } from '../../../shared/components/dialog';
import { RMSButton } from '../../../shared/components/buttons';
import { executeApiAction } from '../../../shared/utility/apiHelper';
import { notify } from 'reducers/NotifierReducer';
import useConfirmDialog from '../../../shared/utility/useConfirmDialog';
import { CONTACT_GRID_COLUMNS } from '../../constants/personContactFormConstants';
import PersonContactDialog from './PersonContactDialog';
import { getPeopleContactMethods, deletePersonContact } from '../../services/userService';

const PersonContactMethodsTab = ({ personId }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const { confirmState, requestConfirm, handleYes, handleNo } = useConfirmDialog();
  const canEditPerson = usePermissions(PERMISSION_RESOURCES.PERSON).Edit;

  const loadContacts = useCallback(async () => {
    if (!personId) return;

    await executeApiAction({
      setLoading,
      dispatch,
      action: () => getPeopleContactMethods(personId),
      onSuccess: (data) => setContacts(data),
    });
  }, [dispatch, personId]);

  useEffect(() => {
    loadContacts();
  }, [loadContacts]);

  const handleAdd = useCallback(() => {
    setEditingContact(null);
    setFormOpen(true);
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingContact(row);
    setFormOpen(true);
  }, []);

  const handleFormClose = useCallback(() => {
    setFormOpen(false);
    setEditingContact(null);
  }, []);

  const handleSaved = useCallback(() => {
    handleFormClose();
    loadContacts();
  }, [handleFormClose, loadContacts]);

  const handleDelete = useCallback(
    (row) => {
      requestConfirm(
        `Are you sure you want to permanently delete this "${
          row.ContactMethod || 'contact'
        }" record?`,
        async () => {
          await executeApiAction({
            setLoading,
            dispatch,
            action: () => deletePersonContact(row.ptsInfoID),
            onSuccess: () => {
              dispatch(notify('Contact deleted successfully', 'success'));
              loadContacts();
            },
          });
        },
        'Delete Contact Method'
      );
    },
    [requestConfirm, dispatch, loadContacts]
  );

  return (
    <Box className="person-names-tab__grid-wrap">
      {loading && <FormSpinner />}
      <Box className="person-addresses-tab__toolbar">
        <RMSButton
          label="Add Contact Method"
          prefixIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={!canEditPerson}
        />
      </Box>

      <CommonDataGrid
        rows={contacts}
        columns={CONTACT_GRID_COLUMNS}
        getRowId={(row) => row.ptsInfoID}
        toolbar={getDataGridToolbarProps({ printTitle: 'Contact Methods' })}
        actions={{
          edit: { visible: true, onClick: handleEdit, disabled: !canEditPerson },
          delete: { visible: true, onClick: handleDelete, disabled: !canEditPerson },
        }}
      />

      {formOpen && (
        <PersonContactDialog
          open={formOpen}
          personId={personId}
          contact={editingContact}
          onClose={handleFormClose}
          onSaved={handleSaved}
        />
      )}

      {confirmState.open && (
        <RMSConfirmationDialog
          open={confirmState.open}
          title={confirmState.title}
          message={confirmState.message}
          onNo={handleNo}
          onYes={handleYes}
        />
      )}
    </Box>
  );
};

export default PersonContactMethodsTab;
